/** Cotizaciones (spec 04): listado con filtros, detalle con triple precio, banner de re-tasa, versiones, pase al POS, anulación. */
import { formatearTasa, Money, type CotizacionDTO, type EstadoCotizacion } from '@mg/shared';
import { ArrowRight, Copy, FileEdit, History, Layers, Printer, RefreshCw, XCircle } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Dinero } from '@/componentes/Dinero.tsx';
import { EnviarCorreo } from '@/componentes/EnviarCorreo.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { veTasa2 } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { cabeceraDeCombo, tituloCombo } from '@/modulos/combos/documento.ts';
import { useAnularCotizacion, useCotizacion, useCotizaciones, useRetasarCotizacion, useVersionesCotizacion } from './api.ts';

const ETIQUETA: Record<EstadoCotizacion, string> = { vigente: 'Vigente', vencida: 'Vencida', facturada: 'Facturada', anulada: 'Anulada' };
const VARIANTE: Record<EstadoCotizacion, 'ok' | 'pendiente' | 'neutro' | 'critico'> = { vigente: 'ok', vencida: 'pendiente', facturada: 'neutro', anulada: 'critico' };
export const EstadoCot = ({ estado }: { estado: EstadoCotizacion }) => <Badge variant={VARIANTE[estado]} data-testid="cot-estado">{ETIQUETA[estado]}</Badge>;
const fecha = (iso: string) => formatearFechaHora(iso, { hour: undefined, minute: undefined });

export function Cotizaciones() {
  const navigate = useNavigate();
  const { puede, usuario } = useAuth();
  // La tasa 2 de cada cotización solo la ve la dueña (pedido de la dueña 2026-10-19)
  const veTasa = veTasa2(usuario);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(1);
  const lista = useCotizaciones({ q: q || undefined, estado: estado || undefined, pagina, limite: 25 });
  const paginas = lista.data ? Math.max(1, Math.ceil(lista.data.total / lista.data.limite)) : 1;
  return (
    <>
      <TituloPagina
        titulo="Cotizaciones"
        subtitulo="Presupuestos con triple precio congelado y vigencia. Se re-tasan al día y pasan directo al POS."
        acciones={puede('cotizaciones.crear') ? <Button asChild data-testid="cot-nueva"><Link to="/cotizaciones/nueva">Nueva cotización</Link></Button> : undefined}
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input placeholder="Número, cliente o RIF…" value={q} onChange={(e) => { setQ(e.target.value); setPagina(1); }} className="w-72" data-testid="cot-buscar" />
        <Select value={estado} onChange={(e) => { setEstado(e.target.value); setPagina(1); }} className="w-44" aria-label="Estado" data-testid="cot-filtro-estado">
          <option value="">Todas</option>
          <option value="vigente">Vigentes</option>
          <option value="vencida">Vencidas</option>
          <option value="facturada">Facturadas</option>
          <option value="anulada">Anuladas</option>
        </Select>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio titulo="Sin cotizaciones" descripcion="Crea la primera con «Nueva cotización»." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">Ver.</TableHead>
                    <TableHead className="text-right">Total $</TableHead>
                    <TableHead className="text-right">Total Bs</TableHead>
                    {veTasa ? <TableHead className="text-right">Tasa 2</TableHead> : null}
                    <TableHead>Vence</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Creada</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.datos.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/cotizaciones/${c.id}`)} data-testid={`cot-${c.numero_formateado}`}>
                      <TableCell className="font-medium">{c.numero_formateado}</TableCell>
                      <TableCell>{c.cliente_razon_social}<span className="block text-[11px] text-muted-foreground">{c.cliente_rif}</span></TableCell>
                      <TableCell className="text-right num">v{c.version}</TableCell>
                      <TableCell className="text-right num">$ {Money.parse(c.total).toDisplay()}</TableCell>
                      <TableCell className="text-right num">Bs {Money.parse(c.total_bs).toDisplay()}</TableCell>
                      {veTasa ? <TableCell className="text-right num">{formatearTasa(c.tasa_paralela)}</TableCell> : null}
                      <TableCell>{c.vence_en ? fecha(c.vence_en) : <span className="text-muted-foreground">Sin vencimiento</span>}</TableCell>
                      <TableCell><EstadoCot estado={c.estado} /></TableCell>
                      <TableCell>{c.documento_numero ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatearFechaHora(c.creado_en)}<span className="block">{c.creado_por_nombre}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{d.total} cotización(es)</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                  <span>{pagina} / {paginas}</span>
                  <Button size="sm" variant="outline" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
                </div>
              </div>
            </>
          )
        }
      </EstadoQuery>
    </>
  );
}

export function DetalleCotizacion() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { puede, usuario } = useAuth();
  // La tasa 2 congelada y la de hoy solo las ve la dueña (pedido de la dueña 2026-10-19)
  const veTasa = veTasa2(usuario);
  const cot = useCotizacion(id ?? null);
  const [verVersiones, setVerVersiones] = useState(false);
  const versiones = useVersionesCotizacion(id ?? null, verVersiones);
  const retasar = useRetasarCotizacion();
  const anular = useAnularCotizacion();
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const puedeEditar = puede('cotizaciones.crear');

  const hacerRetasa = (c: CotizacionDTO) =>
    retasar.mutate(c.id, {
      onSuccess: (r) => (r.sin_cambios ? toast.info('Sin cambios: tasas y precios siguen iguales, no se creó versión') : toast.success(`Re-tasada: versión ${r.cotizacion.version} a tasa ${formatearTasa(r.cotizacion.tasa_bcv)}`)),
      onError: (e) => toast.error('No se pudo re-tasar', { description: mensajeDeError(e) }),
    });

  return (
    <EstadoQuery cargando={cot.isLoading} error={cot.error} datos={cot.data} reintentar={() => void cot.refetch()}>
      {(c) => {
        const doc = { tasa_bcv: c.tasa_bcv, tasa_paralela: c.tasa_paralela, factor_divisa: c.factor_divisa };
        const alerta = c.editable && (c.estado === 'vencida' || c.tasa_movida);
        return (
          <>
            <TituloPagina
              titulo={`Cotización ${c.numero_formateado}`}
              subtitulo={`${c.cliente_razon_social} · ${c.cliente_rif} · lista ${c.lista_codigo} · versión ${c.version} · creada ${formatearFechaHora(c.creado_en)} por ${c.creado_por_nombre}`}
              acciones={
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => window.open(`/imprimir/COT/${c.id}`, '_blank')} data-testid="cot-imprimir"><Printer /> Imprimir / PDF</Button>
                  {/* Al correo registrado del cliente, con el formato de la cotización (pedido de la dueña 2026-10-19) */}
                  {c.estado !== 'anulada' ? <EnviarCorreo tipo="cotizacion" id={c.id} /> : null}
                  {c.editable && puedeEditar ? (
                    <>
                      <Button variant="outline" asChild data-testid="cot-editar"><Link to={`/cotizaciones/${c.id}/editar`}><FileEdit /> Editar</Link></Button>
                      <Button variant="outline" onClick={() => hacerRetasa(c)} cargando={retasar.isPending} data-testid="cot-retasar"><RefreshCw /> Re-tasar</Button>
                      <Button variant="ghost" onClick={() => setAnulando(true)} data-testid="cot-anular"><XCircle /> Anular</Button>
                      <Button onClick={() => navigate(`/pos?cotizacion=${c.id}`)} data-testid="cot-pasar-pos"><ArrowRight /> Pasar al POS</Button>
                    </>
                  ) : null}
                </div>
              }
            />
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <EstadoCot estado={c.estado} />
              <span className="text-sm text-muted-foreground">{c.vence_en ? `Vigencia ${c.vigencia_dias} d · vence ${fecha(c.vence_en)}` : 'Sin vencimiento'} · {veTasa ? `tasa 2 congelada ${formatearTasa(c.tasa_paralela)} · ` : ''}BCV {formatearTasa(c.tasa_bcv)}</span>
              {c.estado === 'facturada' && c.documento_id ? <Link to={`/documentos/${c.documento_id}`} className="text-sm font-medium text-mg-verde underline" data-testid="cot-documento-link">Facturada en {c.documento_numero} · {c.facturada_en ? formatearFechaHora(c.facturada_en) : ''}</Link> : null}
              {c.estado === 'anulada' ? <span className="text-sm text-mg-rojo">Anulada: {c.motivo_anulacion}</span> : null}
            </div>
            {alerta ? (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="cot-banner-retasar">
                <span>
                  {c.estado === 'vencida' && c.vence_en ? `Vencida el ${fecha(c.vence_en)}. ` : ''}
                  {c.tasa_movida ? (veTasa ? `Las tasas cambiaron: cotizada a tasa 2 ${formatearTasa(c.tasa_paralela)} / BCV ${formatearTasa(c.tasa_bcv)}; hoy ${formatearTasa(c.tasa_paralela_hoy)} / ${formatearTasa(c.tasa_bcv_hoy)}. ` : 'Las tasas cambiaron desde que se cotizó. ') : ''}
                  Re-tasa para actualizar precios y tasa del día (se guarda como nueva versión).
                </span>
                {puedeEditar ? <Button size="sm" onClick={() => hacerRetasa(c)} cargando={retasar.isPending}>Re-tasar ahora</Button> : null}
              </div>
            ) : null}
            <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Cant.</TableHead>
                        <TableHead className="text-right">Precio (Bs · $ a BCV · $)</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {c.lineas.map((l, i) => {
                        // Combo o receta (spec 20): su nombre y la medida antes de su primer renglón
                        const combo = cabeceraDeCombo(c.combos, c.lineas, i);
                        return (
                          <Fragment key={l.id}>
                            {combo ? (
                              <TableRow className="bg-mg-verde-claro/60 hover:bg-mg-verde-claro/60" data-testid={`cot-combo-${combo.codigo}`}>
                                <TableCell colSpan={4} className="py-1.5">
                                  <span className="flex items-center gap-2 text-xs font-semibold"><Layers className="h-4 w-4 text-mg-verde" /> {tituloCombo(combo)}</span>
                                </TableCell>
                              </TableRow>
                            ) : null}
                            <TableRow className={l.producto_activo ? '' : 'bg-red-50'} data-testid={`cot-linea-${l.producto_codigo}`}>
                              <TableCell>
                                <p className="font-medium">{l.producto_nombre} {l.producto_activo ? null : <Badge variant="critico">inactivo: quitar o sustituir</Badge>}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {l.producto_codigo} · {l.presentacion_nombre} × {Money.parse(l.unidades_presentacion).toDisplay()} u
                                  {l.descuento_combo ? <span className="font-medium text-mg-verde" data-testid={`cot-linea-descuento-combo-${l.producto_codigo}`}> · Desc. combo −{Money.parse(l.descuento_combo).toDisplay()} %</span> : Money.parse(l.descuento_pct).esPositivo() ? ` · desc. ${l.descuento_pct}%` : ''}
                                </p>
                              </TableCell>
                              <TableCell className="text-right num">{Money.parse(l.cantidad).toDisplay()}</TableCell>
                              <TableCell className="text-right"><Dinero valor={l.precio_unitario} modo="triple" doc={doc} bs={l.precio_unitario_bs} /></TableCell>
                              <TableCell className="text-right"><Dinero valor={l.subtotal} modo="triple" doc={doc} bs={l.subtotal_bs} /></TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card data-testid="cot-totales">
                <CardHeader><CardTitle>Totales</CardTitle><CardDescription>Con y sin IVA, a la tasa congelada.</CardDescription></CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {Money.parse(c.descuento).esPositivo() ? <p className="flex justify-between"><span>{Money.parse(c.descuento_pct).esPositivo() ? `Descuento ${Money.parse(c.descuento_pct).toDisplay()} %` : 'Descuentos de los renglones'}</span><b className="num">$ {Money.parse(c.descuento).toDisplay()}</b></p> : null}
                  {/* Sin IVA y con IVA en Bs, $ a BCV y $ (observación del cliente 24-09-2026) */}
                  <div className="flex justify-between"><span>Total sin IVA</span><Dinero valor={c.base} modo="triple" doc={doc} bs={c.base_bs} /></div>
                  <div className="flex justify-between"><span>IVA {Money.parse(c.iva_pct).toString()}%</span><Dinero valor={c.iva} modo="triple" doc={doc} bs={c.iva_bs} /></div>
                  <div className="flex justify-between border-t pt-2"><span className="font-medium">Total con IVA</span><Dinero valor={c.total} modo="triple" doc={doc} bs={c.total_bs} className="text-base" /></div>
                  {c.observaciones ? <p className="pt-2 text-xs text-muted-foreground">{c.observaciones}</p> : null}
                  <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setVerVersiones((v) => !v)} data-testid="cot-ver-versiones"><History /> {verVersiones ? 'Ocultar' : 'Historial de'} versiones</Button>
                  {verVersiones ? (
                    <ul className="space-y-1 text-xs" data-testid="cot-versiones">
                      {(versiones.data ?? []).map((v) => (
                        <li key={v.id} className="rounded-md border px-2 py-1">
                          <b>v{v.version}</b> · {v.motivo} · tasa {formatearTasa(v.tasa_bcv)} · total $ {Money.parse(v.total).toDisplay()} (Bs {Money.parse(v.total_bs).toDisplay()})
                          <span className="block text-muted-foreground">{formatearFechaHora(v.creado_en)} · {v.usuario_nombre} · {v.lineas.length} línea(s)</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            </div>
            <Dialog open={anulando} onOpenChange={setAnulando}>
              <DialogContent>
                <DialogHeader><DialogTitle>Anular {c.numero_formateado}</DialogTitle><DialogDescription>Queda en el historial como anulada; no se puede importar al POS.</DialogDescription></DialogHeader>
                <Campo etiqueta="Motivo" htmlFor="cot-motivo"><Input id="cot-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="cot-anular-motivo" /></Campo>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAnulando(false)}>Cancelar</Button>
                  <Button disabled={motivo.trim().length < 3} cargando={anular.isPending} onClick={() => anular.mutate({ id: c.id, motivo: motivo.trim() }, { onSuccess: () => { toast.success('Cotización anulada'); setAnulando(false); }, onError: (e) => toast.error(mensajeDeError(e)) })} data-testid="cot-anular-confirmar"><Copy className="hidden" />Anular</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        );
      }}
    </EstadoQuery>
  );
}
